import { useTranslate } from "@refinedev/core";
import { SaveButton, useForm } from "@refinedev/antd";
import { useApiUrl, useNotification } from "@refinedev/core";
import { ButtonProps, Flex, Form, UploadFile } from "antd";
import { ReactNode, RefObject, useEffect, useRef, useState } from "react";
import { PaperClipOutlined } from "@ant-design/icons";
import { DocumentType, MediaProject } from "pages/media/types";
import UploadFiles, { UploadFilesRef } from "components/uploader/UploadFiles/";
import { useDocumentMimeTypes } from "hooks/useDocumentMimeTypes";
import { filterUniqueFiles } from "../helpers/project-create-wizard";
import { useConfigurationValue } from "hooks/useSystemConfiguration/";
import { Configurations } from "@/constants";

type FormData = {
  document_type: string;
  sub_category: string;
  source_url: string;
  filename: string;
};

const MAX_UPLOAD_LIMIT = 25;

export const ProjectDocumentUpload = ({
  projectId,
  onUploadSuccess,
  onSubmit,
  onUploadAbort,
  submitButtonProps,
  submitButtonLabel,
  showSuccessNotification = true,
  acceptedMimeTypes,
  uploadImmediately,
  uploadComponentRef,
  onFilesAdded,
  title,
  hint,
  style,
  customAssetRequestProperties,
  sortable = false,
  onSort,
}: {
  projectId?: string;
  initialFile?: UploadFile;
  onUploadSuccess?: () => void;
  onUploadAbort?: () => void;
  onSubmit?: (filenames: string[]) => Promise<MediaProject>;
  submitButtonProps?: ButtonProps;
  submitButtonLabel?: string;
  showSuccessNotification?: boolean;
  acceptedMimeTypes?: string;
  onFilesAdded?: (
    fileList: UploadFile[],
    detectedLanguage?: string | null
  ) => Promise<void>;
  uploadImmediately?: boolean;
  uploadComponentRef?: RefObject<UploadFilesRef | undefined>;
  title?: ReactNode;
  hint?: ReactNode;
  style?: React.CSSProperties;
  customAssetRequestProperties?: Record<string, unknown>;
  sortable?: boolean;
  onSort?: (fileList: UploadFile[]) => void;
}) => {
  const t = useTranslate();
  const [isUploading, setIsUploading] = useState(false);
  const { open } = useNotification();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const uploadFilesRef = useRef<UploadFilesRef>(null);
  const { allMimeTypes } = useDocumentMimeTypes();
  const mimeTypes = acceptedMimeTypes || allMimeTypes;
  const { formProps, saveButtonProps, form } = useForm<FormData>({
    // won't be used, the form will be submitted manually to support
    // custom file upload
    action: "create",
    resource: "media/documents",
    onMutationSuccess: (_data: unknown) => undefined,
  });
  const apiUrl = useApiUrl();
  const newProjectRef = useRef<MediaProject | undefined>(undefined);
  const uploadLimitValue = useConfigurationValue<number>(
    Configurations.UPLOAD_LIMIT
  );
  const uploadLimit = uploadLimitValue ?? MAX_UPLOAD_LIMIT;

  const [isMultipleFiles, setIsMultipleFiles] = useState(false); // to persist multiple files uploading when user removes and adds a file

  const handleFinish = async () => {
    setIsUploading(true);
    const newProject =
      onSubmit &&
      !projectId &&
      (await onSubmit(files.map((file) => file.name)));
    if (newProject) {
      newProjectRef.current = newProject;
    }
    try {
      const id = projectId ?? (newProject ? newProject.id : undefined);
      if (!id) throw new Error("Missing project id");
      await (uploadComponentRef ?? uploadFilesRef).current?.upload(
        (fileName: string) =>
          `${apiUrl}/media/projects/${id}/documents/${fileName}`
      );
      onUploadSuccess?.();
      if (showSuccessNotification) {
        const fileCount = files.length;
        const fileNames = files.map((file) => file.name);
        open?.({
          type: "success",
          message: t(
            "projects.components.ProjectDocumentUpload.uploadSuccessful"
          ),
          description: t(
            "projects.components.ProjectDocumentUpload.processingDocuments",
            {
              count: fileCount,
              docNames: fileNames.join(", "),
            }
          ),
          key: `upload-success-${fileCount}`,
        });
      }
    } catch (error) {
      console.error(error);
      notifyUploadError();
    }

    // Reset flags and lists once notifications have been handled
    setIsUploading(false);
    form.resetFields();
    setFiles([]);
  };

  const notifyUploadError = () => {
    open?.({
      type: "error",
      message: t("projects.components.ProjectDocumentUpload.uploadFailed"),
      description: t(
        "projects.components.ProjectDocumentUpload.uploadFailedDescription"
      ),
    });
  };

  useEffect(() => {
    if (projectId && uploadImmediately && !isMultipleFiles) {
      form.submit();
    }
  }, [form, projectId, uploadImmediately, isMultipleFiles]);

  const assetRequestProperties = {
    document_type: "Unknown" as DocumentType, // hardcoded until we put back the Select
    project_id: projectId,
    ...customAssetRequestProperties,
  };

  const handleOnSaveButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (saveButtonProps?.onClick) {
      saveButtonProps.onClick(e);
      return;
    }
    if (submitButtonProps?.onClick) {
      submitButtonProps.onClick(e);
      return;
    }
    e.preventDefault();
    if (projectId) {
      form.submit();
    } else if (files.length > 1 && onSubmit) {
      //create the project
      onSubmit(files.map((file) => file.name))
        .then(() => {
          form.submit();
        })
        .catch((error) => {
          console.error(error);
          notifyUploadError();
        });
    } else {
      return;
    }
  };

  const handleOnFilesAdded = (fileList: UploadFile[]) => {
    setFiles((prevFiles) => {
      return filterUniqueFiles([...prevFiles, ...fileList]);
    });

    if (!isMultipleFiles && fileList.length > 1) {
      setIsMultipleFiles(true);
    }
    onFilesAdded?.(fileList).catch((error) => {
      console.error(error);
    });
  };

  return (
    <Form
      {...formProps}
      layout="vertical"
      onFinish={() => {
        handleFinish().catch((error) => {
          console.error(error);
        });
      }}
      disabled={isUploading}
    >
      <Flex vertical gap={5} style={{ marginTop: 10 }}>
        <UploadFiles
          ref={uploadComponentRef ?? uploadFilesRef}
          onFilesAdded={handleOnFilesAdded}
          onAbort={() => {
            onUploadAbort?.();
            setIsUploading(false);
          }}
          onUploadComplete={(_files, _results) => {
            onUploadSuccess?.();
          }}
          itemRequestProperties={assetRequestProperties}
          maxCount={35}
          maxFileSize={uploadLimit}
          accept={acceptedMimeTypes}
          name="files"
          multiple={true}
          acceptedMimeTypes={mimeTypes}
          title={title}
          hint={hint}
          style={style}
          showUploadList={{ showRemoveIcon: !uploadImmediately }}
          showErrorNotification={false}
          sortable={sortable}
          onRemove={(file) => {
            setFiles((prevFiles) => {
              const newFiles = prevFiles.filter((f) => f.uid !== file.uid);
              if (!newFiles.length) {
                // last file removed
                onFilesAdded?.(newFiles, null).catch((error) => {
                  console.error(error);
                });
                setIsMultipleFiles(false);
              }

              return newFiles;
            });
          }}
          onSort={(fileList) => {
            setFiles(fileList);
            onSort?.(fileList);
          }}
        />
      </Flex>
      <SaveButton
        {...saveButtonProps}
        type="primary"
        size="large"
        shape="round"
        style={{ marginTop: 30, ...submitButtonProps?.style }}
        icon={<PaperClipOutlined />}
        {...submitButtonProps}
        onClick={handleOnSaveButtonClick}
        disabled={isUploading || files.length === 0}
      >
        {submitButtonLabel ?? "Attach"}
      </SaveButton>
    </Form>
  );
};
